import React from 'react';
import type { FieldErrors } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import { FormProgressStepper } from '~widgets/application-form/form-progress-stepper';

import { analytics } from '~entities/amplitude-analytics';
import type { SubmitAction } from '~entities/application';
import {
  shouldAskForAdditionalId,
  type IdDocumentType,
} from '~entities/person';

import { useFormInitialize, useOnMountEffect } from '~shared/hooks';
import { getFieldsErrors } from '~shared/lib/get-fields-errors';
import { FormFooter } from '~shared/ui/form-footer';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Screen } from '~shared/ui/screen';
import { ScreenTitle } from '~shared/ui/screen-title';

import { MaskedInput } from '@breeze/rhf-adapters';

import type {
  MainIdDocumentTypeValue,
  OtherIdDocumentTypeValue,
} from '../../model/document-type';
import { REQUIRED_NUMBER_ERROR } from '../constants';
import { idDocumentMetaMap } from '../lib';

export type FormValues = {
  idType: IdDocumentType;
  idNumber?: string;
};

type Props = Readonly<{
  initialValue?: Partial<FormValues>;
  currentIdType: MainIdDocumentTypeValue | OtherIdDocumentTypeValue;
  onPrev: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  onSubmit: (values: FormValues, action?: SubmitAction) => void;
  onFieldCompleted?: (
    value: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  withRejectAction?: boolean;
  isSubmitting?: boolean;
  applicationId?: string;
}>;

export const IdDocumentNumber: React.FC<Props> = ({
  onSubmit,
  initialValue,
  currentIdType = 'UMID',
  onPrev,
  onFieldCompleted,
  withRejectAction,
  isSubmitting = false,
  applicationId,
}) => {
  // we need to reset id document data if document type has been changed
  const realInitialValue =
    initialValue?.idType === currentIdType
      ? initialValue
      : { idType: currentIdType, idNumber: '' };
  const methods = useForm<FormValues>({
    defaultValues: realInitialValue,
    mode: 'all',
  });
  const { control, getValues, handleSubmit, register } = methods;

  useFormInitialize(methods, realInitialValue);

  const idDocumentMeta = idDocumentMetaMap[currentIdType];

  useOnMountEffect(() => {
    if (applicationId) {
      analytics.track.ponApplicationKycMainIdNumberView(applicationId);
    }
  });

  const handleClickButtonPrev = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onPrev(getValues(), actualErrors);
  };

  const handleBlur = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onFieldCompleted?.(getValues(), actualErrors);
  };

  const handleFormSubmit = (values: FormValues, action?: SubmitAction) => {
    onSubmit(values, action);

    if (applicationId) {
      analytics.track.ponApplicationKycMainIdNumberNextClick(applicationId);
    }
  };

  const isLastScreen = !shouldAskForAdditionalId(currentIdType);

  return (
    <form
      onBlur={handleBlur}
      onSubmit={handleSubmit((values) => handleFormSubmit(values, 'SUCCESS'))}
    >
      <Screen
        header={<HeaderWithLogo />}
        footer={
          <FormFooter
            onClickPrev={handleClickButtonPrev}
            isLastScreen={isLastScreen}
            isSubmitting={isSubmitting}
            onSubmitWithAction={
              withRejectAction
                ? (e, action) =>
                    handleSubmit((v) => handleFormSubmit(v, action))(e)
                : undefined
            }
          />
        }
      >
        <div className="mb-5">
          <ScreenTitle
            title="Main identity document"
            icon={<FormProgressStepper step="identity" />}
          />
        </div>

        <input {...register('idType')} hidden />
        <Controller
          control={control}
          name="idNumber"
          shouldUnregister
          rules={{
            required: REQUIRED_NUMBER_ERROR,
            ...idDocumentMeta.validators,
          }}
          render={(fieldProps) => (
            <MaskedInput
              {...fieldProps}
              field={{
                ...fieldProps.field,
                // TODO: check why there can be null value after changing type
                value: fieldProps.field.value || '',
                onChange: (event, params) => {
                  fieldProps.field.onChange(
                    idDocumentMeta.transform(event, params)
                  );
                },
              }}
              partialPlaceholder
              label={idDocumentMeta.label}
              mask={idDocumentMeta.mask.value}
              maskTransitions={idDocumentMeta.mask.transitions}
              placeholder={idDocumentMeta.placeholder}
            />
          )}
        />
      </Screen>
    </form>
  );
};
